import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { QrCode, MapPin, Check, AlertTriangle, Plane, Shield, Clock, X, Utensils, Coffee, Home, LogIn, LogOut } from "lucide-react";
import WorkModeBadge from "./WorkModeBadge";

const haversine = (lat1, lng1, lat2, lng2) => {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
};

export default function EmployeeCheckinTab() {
  const [qrs, setQrs] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeQr, setActiveQr] = useState(null); // QR selezionato per timbrare
  const [position, setPosition] = useState(null);
  const [posError, setPosError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [outOfArea, setOutOfArea] = useState(null); // legacy state — kept to avoid breaking refs; not used in UI anymore
  const [trasfertaPrompt, setTrasfertaPrompt] = useState(null);
  // Pre-flight out-of-area prompt: chiede smartworking/trasferta + nota PRIMA di salvare
  const [ooaPrompt, setOoaPrompt] = useState(null); // {kind, distance_m, qr, note}

  const load = async () => {
    setLoading(true);
    try {
      const [q, a] = await Promise.all([
        api.get("/qr"),
        api.get("/attendances?limit=10"),
      ]);
      setQrs(q.data);
      // filtra le timbrature dell'utente corrente lato client
      const me = JSON.parse(localStorage.getItem("tb_user") || "{}");
      setRecent((a.data || []).filter(x => !me?.id || x.user_id === me.id).slice(0, 8));
    } catch { /* noop */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Quick-tap kind selector (skips dialog and submits with current position)
  const [quickKind, setQuickKind] = useState("in");

  const startCheckin = (qr, preferredKind = "in") => {
    setQuickKind(preferredKind);
    askPosition(qr);
  };

  const askPosition = (qr) => {
    setActiveQr(qr);
    setPosition(null);
    setPosError(null);
    setOutOfArea(null);
    if (!("geolocation" in navigator)) {
      setPosError("La geolocalizzazione non è supportata dal tuo browser");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) => setPosition({ lat: p.coords.latitude, lng: p.coords.longitude, acc: p.coords.accuracy }),
      (err) => setPosError(err.message || "Permesso negato"),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const distanceFromQr = position && activeQr
    ? Math.round(haversine(position.lat, position.lng, activeQr.latitude, activeQr.longitude))
    : null;
  const inRadius = distanceFromQr !== null && distanceFromQr <= activeQr.radius_m;

  const submitCheckin = async (kind, opts = {}) => {
    if (!position || !activeQr) return;
    // Pre-flight: se fuori area E non abbiamo già una scelta, mostra il dialog smart/trasferta
    if (!opts.skip_out_check && !opts.work_mode) {
      const dist = Math.round(haversine(position.lat, position.lng, activeQr.latitude, activeQr.longitude));
      if (dist > activeQr.radius_m) {
        setOoaPrompt({ kind, distance_m: dist, qr: activeQr, note: "" });
        return;
      }
    }
    setSubmitting(true);
    try {
      const payload = {
        qr_id: activeQr.id,
        latitude: position.lat,
        longitude: position.lng,
        kind,
        trasferta: opts.work_mode === "trasferta" || trasfertaPrompt === "yes",
      };
      if (opts.work_mode) payload.work_mode = opts.work_mode;
      if (opts.note != null) payload.note = String(opts.note).slice(0, 500);
      const r = await api.post("/attendance/checkin", payload);
      const data = r.data;
      if (!data.valid) {
        // Mostra pop-up "Anomalia segnalata all'admin" DOPO la scelta smart/trasferta
        setOutOfArea({
          distance: data.distance_m,
          qr: activeQr,
          kind,
          work_mode: opts.work_mode || null,
          note: opts.note || "",
          notified_admins: data.notified_admins || [],
        });
      } else {
        const KIND_LABELS = { in: "Entrata", out: "Uscita", lunch_start: "Pausa pranzo", lunch_end: "Fine pausa" };
        toast.success(`${KIND_LABELS[kind] || kind} registrata · ${data.distance_m}m dal QR`);
        setActiveQr(null);
        setPosition(null);
        load();
      }
      if (data.trasferta_session_id) {
        toast.info("Trasferta avviata · tracking GPS attivo");
      }
      if (data.closed_trasferta_session) {
        toast.info("Sessione trasferta chiusa");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore timbratura");
    } finally { setSubmitting(false); setTrasfertaPrompt(null); }
  };

  const confirmOoa = async (work_mode) => {
    if (!ooaPrompt) return;
    const { kind, note } = ooaPrompt;
    setOoaPrompt(null);
    await submitCheckin(kind, { work_mode, note, skip_out_check: true });
  };

  const closeOutOfArea = () => {
    setOutOfArea(null);
    setActiveQr(null);
    setPosition(null);
    load();
  };

  return (
    <div data-testid="employee-checkin-tab" className="space-y-6">
      <div className="card-smart p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-12 h-12 rounded-xl bg-[#1E2347] flex items-center justify-center flex-shrink-0">
            <QrCode className="w-6 h-6 text-[#3B82F6]"/>
          </div>
          <div>
            <h2 className="font-display text-2xl">Timbratura</h2>
            <p className="text-sm text-[#757575]">Clicca il QR della tua sede per timbrare entrata o uscita. Verrà verificata la tua posizione GPS.</p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-[#757575] py-8">Caricamento sedi…</div>
      ) : qrs.length === 0 ? (
        <div className="card-smart p-8 text-center text-[#757575]">
          <QrCode className="w-12 h-12 mx-auto mb-3 opacity-30"/>
          <div>Nessuna sede QR configurata. Contatta l'amministratore della tua azienda.</div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {qrs.map((q) => (
            <div key={q.id} className="card-smart p-5 hover:border-[#3B82F6] hover:shadow-lg transition group" data-testid={`emp-checkin-card-${q.id}`}>
              <button onClick={() => startCheckin(q, "in")} data-testid={`emp-checkin-${q.id}`} className="w-full text-left">
                <img src={`data:image/png;base64,${q.png_base64 || ""}`} alt={q.label}
                  className="w-full aspect-square bg-white rounded-lg group-hover:scale-105 transition"
                  style={{ imageRendering: "pixelated" }}/>
                <div className="mt-3">
                  <div className="font-display text-lg leading-tight">{q.label}</div>
                  {(q.address || q.city) && (
                    <div className="text-xs text-[#444] mt-1 flex items-start gap-1">
                      <MapPin className="w-3 h-3 mt-0.5 text-[#3B82F6] flex-shrink-0"/>
                      <span>{q.address}{q.address && q.city ? ", " : ""}{q.city}{q.province ? ` (${q.province})` : ""}</span>
                    </div>
                  )}
                </div>
              </button>
              <div className="grid grid-cols-2 gap-2 mt-3">
                <Button onClick={() => startCheckin(q, "in")} data-testid={`emp-quick-in-${q.id}`} className="rounded-full bg-[#3B82F6] hover:bg-[#7C3AED] h-9 text-xs">
                  Entrata
                </Button>
                <Button onClick={() => startCheckin(q, "out")} data-testid={`emp-quick-out-${q.id}`} variant="outline" className="rounded-full h-9 text-xs">
                  Uscita
                </Button>
                <Button onClick={() => startCheckin(q, "lunch_start")} data-testid={`emp-quick-lunch-start-${q.id}`} variant="outline" className="rounded-full h-9 text-xs border-amber-400 text-amber-700 hover:bg-amber-50">
                  <Utensils className="w-3.5 h-3.5 mr-1"/> Pausa pranzo
                </Button>
                <Button onClick={() => startCheckin(q, "lunch_end")} data-testid={`emp-quick-lunch-end-${q.id}`} variant="outline" className="rounded-full h-9 text-xs border-amber-400 text-amber-700 hover:bg-amber-50">
                  <Coffee className="w-3.5 h-3.5 mr-1"/> Fine pausa
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {recent.length > 0 && (
        <div className="card-smart p-5">
          <div className="text-xs uppercase tracking-widest text-[#757575] font-bold mb-3 flex items-center gap-1">
            <Clock className="w-3 h-3"/> Le tue ultime timbrature
          </div>
          <ul className="divide-y divide-[#1F2540]">
            {recent.map((a) => (
              <li key={a.id} className="py-2.5 text-sm">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">{({in:"Entrata", out:"Uscita", lunch_start:"Pausa pranzo", lunch_end:"Fine pausa"})[a.kind] || a.kind}</span>
                    <span className="text-[#757575] truncate"> · {new Date(a.timestamp).toLocaleString("it-IT")} · {a.qr_label}</span>
                    <WorkModeBadge mode={a.work_mode} size="xs"/>
                  </div>
                  {a.valid
                    ? <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full flex items-center gap-1"><Check className="w-3 h-3"/>OK</span>
                    : <span className="text-[10px] bg-red-100 text-red-700 px-2 py-0.5 rounded-full flex items-center gap-1"><X className="w-3 h-3"/>Fuori area</span>}
                </div>
                {a.note && <div className="text-xs text-[#444] italic mt-1 line-clamp-2" title={a.note}>“{a.note}”</div>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Dialog conferma posizione */}
      <Dialog open={!!activeQr && !ooaPrompt && !outOfArea} onOpenChange={(o) => { if (!o) { setActiveQr(null); setPosition(null); } }}>
        <DialogContent className="rounded-2xl max-w-md" data-testid="confirm-position-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <MapPin className="w-6 h-6 text-[#3B82F6]"/> Conferma {({in:"entrata", out:"uscita", lunch_start:"pausa pranzo", lunch_end:"fine pausa"})[quickKind] || "timbratura"}
            </DialogTitle>
          </DialogHeader>
          {activeQr && (
            <div className="space-y-3">
              {/* Tipo timbratura selezionato — evidenza visiva */}
              <div className={`rounded-xl p-3 border-2 flex items-center gap-3 ${
                quickKind === "in" ? "bg-emerald-50 border-emerald-300" :
                quickKind === "out" ? "bg-[#0A0A0A] border-[#0A0A0A] text-white" :
                "bg-amber-50 border-amber-300"
              }`} data-testid={`confirm-kind-banner-${quickKind}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  quickKind === "in" ? "bg-emerald-500 text-white" :
                  quickKind === "out" ? "bg-white text-[#0A0A0A]" :
                  "bg-amber-500 text-white"
                }`}>
                  {quickKind === "in" ? <LogIn className="w-5 h-5"/> :
                   quickKind === "out" ? <LogOut className="w-5 h-5"/> :
                   quickKind === "lunch_start" ? <Utensils className="w-5 h-5"/> :
                   <Coffee className="w-5 h-5"/>}
                </div>
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-widest opacity-70">Stai timbrando</div>
                  <div className="font-display text-xl leading-none mt-0.5">{({in:"Entrata", out:"Uscita", lunch_start:"Pausa pranzo", lunch_end:"Fine pausa"})[quickKind] || quickKind}</div>
                </div>
              </div>
              <div className="bg-[#0A0E27] border border-[#1F2540] rounded-xl p-3">
                <div className="font-semibold text-sm">{activeQr.label}</div>
                {(activeQr.address || activeQr.city) && <div className="text-xs text-[#757575]">{activeQr.address} {activeQr.city}{activeQr.province ? ` (${activeQr.province})` : ""}</div>}
                <div className="text-xs text-[#757575] mt-1">Raggio consentito: <strong>{activeQr.radius_m}m</strong></div>
              </div>
              {posError ? (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0"/>
                  <div>{posError}<br/><span className="text-xs">Concedi il permesso di geolocalizzazione e riprova.</span></div>
                </div>
              ) : !position ? (
                <div className="text-center py-6">
                  <div className="inline-block w-8 h-8 border-4 border-[#1E2347] border-t-[#3B82F6] rounded-full animate-spin mb-2"/>
                  <div className="text-sm text-[#757575]">Rilevamento GPS in corso…</div>
                </div>
              ) : (
                <div className={`rounded-xl p-3 ${inRadius ? "bg-green-50 border border-green-200" : "bg-amber-50 border border-amber-300"}`}>
                  <div className="flex items-center gap-2 font-semibold text-sm">
                    {inRadius
                      ? <><Check className="w-5 h-5 text-green-600"/> Sei nell'area consentita</>
                      : <><AlertTriangle className="w-5 h-5 text-amber-600"/> Sei fuori dall'area</>}
                  </div>
                  <div className="text-xs text-[#444] mt-1">Distanza dal QR: <strong>{distanceFromQr}m</strong> · Precisione GPS: ~{Math.round(position.acc || 0)}m</div>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2 flex-col-reverse sm:flex-row">
            <Button variant="ghost" onClick={() => { setActiveQr(null); setPosition(null); }} className="rounded-full w-full sm:w-auto" data-testid="confirm-cancel-btn">Annulla</Button>
            <Button
              disabled={!position || submitting}
              onClick={() => submitCheckin(quickKind)}
              data-testid={`confirm-${quickKind.replace("_", "-")}-btn`}
              style={{ color: "#FFFFFF" }}
              className={`rounded-full w-full sm:flex-1 h-12 text-base font-semibold !text-white [&_*]:!text-white [&_svg]:!text-white ${
                quickKind === "in" ? "bg-emerald-600 hover:bg-emerald-700" :
                quickKind === "out" ? "bg-[#0A0A0A] hover:bg-black" :
                "bg-amber-500 hover:bg-amber-600"
              }`}
            >
              {submitting ? "Timbratura…" : (
                <>
                  {quickKind === "in" ? <LogIn className="w-5 h-5 mr-2 text-white"/> :
                   quickKind === "out" ? <LogOut className="w-5 h-5 mr-2 text-white"/> :
                   quickKind === "lunch_start" ? <Utensils className="w-5 h-5 mr-2 text-white"/> :
                   <Coffee className="w-5 h-5 mr-2 text-white"/>}
                  <span className="text-white">Timbra {({in:"ENTRATA", out:"USCITA", lunch_start:"PAUSA PRANZO", lunch_end:"FINE PAUSA"})[quickKind] || ""}</span>
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pre-save FUORI AREA: chiedi smart working / trasferta + nota */}
      <Dialog open={!!ooaPrompt} onOpenChange={(o) => { if (!o) setOoaPrompt(null); }}>
        <DialogContent className="rounded-2xl max-w-md max-h-[92vh] overflow-y-auto" data-testid="ooa-prompt-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl md:text-2xl flex items-center gap-2">
              <Shield className="w-6 h-6 text-amber-600"/> Sei fuori dalla sede
            </DialogTitle>
          </DialogHeader>
          {ooaPrompt && (
            <div className="space-y-4 text-sm">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs leading-relaxed text-amber-900">
                <div className="font-bold mb-1">📍 Distanza: {ooaPrompt.distance_m}m dal QR "{ooaPrompt.qr.label}"</div>
                <div>Il raggio consentito è di {ooaPrompt.qr.radius_m}m. Indica la tua modalità di lavoro per registrare comunque la timbratura:</div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#999] font-bold">Modalità di lavoro</Label>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <button
                    type="button"
                    onClick={() => confirmOoa("smartworking")}
                    data-testid="ooa-smartworking"
                    className="rounded-2xl border-2 border-emerald-300 hover:border-emerald-500 hover:bg-emerald-50 transition p-4 flex flex-col items-center gap-2 text-center"
                  >
                    <Home className="w-7 h-7 text-emerald-600"/>
                    <div className="font-semibold text-emerald-900">Smart working</div>
                    <div className="text-[11px] text-[#757575]">Lavoro da remoto/casa</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => confirmOoa("trasferta")}
                    data-testid="ooa-trasferta"
                    className="rounded-2xl border-2 border-[#3B82F6]/40 hover:border-[#3B82F6] hover:bg-[#1E2347] transition p-4 flex flex-col items-center gap-2 text-center"
                  >
                    <Plane className="w-7 h-7 text-[#3B82F6]"/>
                    <div className="font-semibold text-[#0A0A0A]">Trasferta</div>
                    <div className="text-[11px] text-[#757575]">Cliente/cantiere {ooaPrompt.kind === "in" ? "· tracking GPS" : ""}</div>
                  </button>
                </div>
              </div>
              <div>
                <Label htmlFor="ooa-note-emp" className="text-xs uppercase tracking-widest text-[#999] font-bold">Nota (facoltativa)</Label>
                <textarea
                  id="ooa-note-emp"
                  data-testid="ooa-note"
                  rows={3}
                  maxLength={500}
                  value={ooaPrompt.note}
                  onChange={(e) => setOoaPrompt({ ...ooaPrompt, note: e.target.value })}
                  placeholder="Es. visita cliente Acme · cantiere Roma · lavoro da casa giovedì…"
                  className="w-full mt-1 rounded-xl border border-[#1F2540] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6] resize-none"
                />
                <div className="text-[10px] text-[#999] mt-1 text-right">{ooaPrompt.note.length}/500</div>
              </div>
              <div className="text-[11px] text-[#757575] leading-snug flex items-start gap-1">
                <Shield className="w-3 h-3 mt-0.5 flex-shrink-0"/>
                <span>La timbratura verrà registrata come "fuori area" con la modalità da te dichiarata e la nota. L'amministratore riceverà una notifica.</span>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 flex-col-reverse sm:flex-row">
            <Button onClick={() => setOoaPrompt(null)} variant="ghost" data-testid="ooa-cancel" className="rounded-full">Annulla</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pop-up "Anomalia segnalata all'amministratore" — appare DOPO la scelta smart/trasferta */}
      <Dialog open={!!outOfArea} onOpenChange={(o) => { if (!o) closeOutOfArea(); }}>
        <DialogContent className="rounded-2xl max-w-md border-2 border-red-500" data-testid="anomaly-reported-dialog">
          <div className="text-center py-4">
            <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4 animate-pulse">
              <AlertTriangle className="w-10 h-10 text-red-600"/>
            </div>
            <h2 className="font-display text-2xl text-red-700 mb-2">Anomalia segnalata</h2>
            <p className="text-sm text-[#444] mb-3">
              La timbratura è stata registrata <strong>fuori dall'area consentita</strong> e segnalata automaticamente all'amministratore.
            </p>
            {outOfArea && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-left text-sm space-y-1 mb-3">
                <div><span className="text-[#757575]">Sede QR:</span> <strong>{outOfArea.qr.label}</strong></div>
                <div><span className="text-[#757575]">Distanza:</span> <strong className="text-red-700">{outOfArea.distance}m</strong> (max {outOfArea.qr.radius_m}m)</div>
                <div><span className="text-[#757575]">Tipo:</span> {({in:"Entrata", out:"Uscita", lunch_start:"Pausa pranzo", lunch_end:"Fine pausa"})[outOfArea.kind] || outOfArea.kind}</div>
                {outOfArea.work_mode && (
                  <div className="flex items-center gap-2"><span className="text-[#757575]">Modalità dichiarata:</span> <WorkModeBadge mode={outOfArea.work_mode}/></div>
                )}
                {outOfArea.note && (
                  <div className="pt-2 border-t border-red-200 mt-2">
                    <div className="text-[10px] uppercase tracking-widest text-[#757575] font-bold mb-0.5">Tua nota</div>
                    <div className="italic text-[#0A0A0A]">“{outOfArea.note}”</div>
                  </div>
                )}
              </div>
            )}
            <div className="bg-[#1E2347] border border-[#3B82F6]/30 rounded-xl p-3 text-xs text-[#444] flex items-start gap-2 text-left">
              <Shield className="w-4 h-4 text-[#3B82F6] mt-0.5 flex-shrink-0"/>
              <div>
                {outOfArea?.notified_admins?.length > 0 ? (
                  <>
                    Notifica inviata a: {outOfArea.notified_admins.slice(0, 3).map((a, i) => (
                      <span key={a.email || i}>
                        <strong className="text-[#0A0A0A]">{a.full_name}</strong>{i < Math.min(outOfArea.notified_admins.length, 3) - 1 ? ", " : ""}
                      </span>
                    ))}
                    {outOfArea.notified_admins.length > 3 && <span> e altri {outOfArea.notified_admins.length - 3}</span>}.
                    <div className="mt-1">L'email contiene tutti i dettagli della timbratura, la modalità da te dichiarata e la nota.</div>
                  </>
                ) : (
                  <>L'amministratore della tua azienda ha ricevuto un'email di notifica con i dettagli della timbratura, la modalità da te dichiarata e la nota.</>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={closeOutOfArea} data-testid="close-anomaly-reported" className="w-full rounded-full bg-red-600 hover:bg-red-700">Ho capito</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
